import getTopicCurrPrice from '@helpers/getTopicCurrPrice';
import renderEpisodeLength from '@helpers/renderEpisodeLength';
import useAPI from '@hooks/useAPI';
import { useScrollPosition } from '@hooks/useScrollPosition';
import useSession from '@hooks/useSession';
import Episode from '@models/Episode';
import { Podcast } from '@models/Podcast';
import { Topic } from '@models/Topic';
import { useRouter } from 'next/router';
import React, { useRef, useState } from 'react';

function TopicsAndEpisodes(props: {
  topics: Topic[];
  episodes: Episode[];
  podcast: Podcast;
}) {
  const session = useSession();
  const router = useRouter();
  const API = useAPI();
  const { topics } = props;
  const [isRefreshing, setRefreshing] = useState(false);
  const [offset, setOffset] = useState(0);
  const [episodes, setEpisodes] = useState(props.episodes);
  const [isEnd, setEnd] = useState(false);

  const refreshingRef = useRef<boolean>();
  const offsetRef = useRef<number>();
  const endRef = useRef<boolean>();
  refreshingRef.current = isRefreshing;
  offsetRef.current = offset;
  endRef.current = isEnd;

  useScrollPosition(
    async ({ currPos }) => {
      if (
        document.body.offsetHeight - window.innerHeight + currPos.y < 700 &&
        !refreshingRef.current &&
        !endRef.current
      ) {
        await getNextPage();
      }
    },
    [],
    null,
    false,
    0
  );

  async function getNextPage() {
    if (isRefreshing) return;
    try {
      setRefreshing(true);
      const newEpisodes = await API.episode.getMany(
        props.podcast.id,
        offsetRef.current + 1
      );
      if (newEpisodes.length === 0) {
        setEnd(true);
      }
      setEpisodes((eps) => [...eps, ...newEpisodes]);
      setOffset(offsetRef.current + 1);
      setRefreshing(false);
    } catch (err) {
      console.error(err);
    }
  }

  const renderTopicTitle = (topic: Topic) => {
    if (!process.browser) return;
    if (window.innerWidth < 479) {
      if (topic.name.length > 30) return topic.name.substring(0, 29) + ' ...';
      return topic.name;
    }

    if (topic.name.length > 28) return topic.name.substring(0, 27) + ' ...';
    return topic.name;
  };

  const renderTopicDescription = (topic: Topic) => {
    if (topic.name.length <= 15 && topic.description.length > 57)
      return topic.description.substring(0, 56) + ' ...';

    if (topic.description.length > 36)
      return topic.description.substring(0, 35) + ' ...';

    return topic.description;
  };

  return (
    <div>
      {topics.length > 0 ? (
        <div className="section-4">
          <div className="container-4 w-container">
            <h2 className="cnannel-episode">課程清單</h2>
            <div className="title-underline"/>
            <div role="list" className="list-6 w-list-unstyled">
              {topics.map((topic) => (
                <div
                  className="a-topic"
                  onClick={() =>
                    router.push(
                      '/topic/' +
                      topic.id +
                      (getTopicCurrPrice(topic)[0] === 0 ? '/listen' : '')
                    )
                  }
                  key={topic.id}
                >
                  <div className="topic-banner-wrapper">
                    <div className="fixed-ratio-image-container">
                      <img
                        src={topic.banner || topic.image}
                        loading="lazy"
                        alt=""
                        className="topic-banner"
                      />
                    </div>
                  </div>
                  <div className="div-block-50">
                    <div className="title-and-description">
                      <div className="topic-name">
                        {renderTopicTitle(topic)}
                      </div>
                      <div className="topic-description">
                        {renderTopicDescription(topic)}
                      </div>
                    </div>
                    <div className="topic-info-2">
                      <div className="topic-price">
                        <div className="text-block-7-copy">
                          {topic.purchase_count} 人已訂購
                        </div>

                        {getTopicCurrPrice(topic)[1] > 0 ? (
                          <div className="price-01">
                            NT${getTopicCurrPrice(topic)[1]}
                          </div>
                        ) : (
                          <div className="price-01">免費收聽</div>
                        )}

                        {getTopicCurrPrice(topic)[1] <
                        getTopicCurrPrice(topic)[0] && (
                          <div className="price-02">
                            NT${getTopicCurrPrice(topic)[0]}
                          </div>
                        )}
                      </div>
                      <div className="tag-of-topic">
                        {topic.sku?.metadata?.except_total_audio_length > 0 && (
                          <p className="grades">
                            共
                            {topic.sku.metadata.except_total_audio_length}{' '}
                            分鐘
                          </p>
                        )}

                        <p className="number-of-purchasers">
                          {topic.purchase_count} 人已訂購
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div/>
      )}

      <div className="section-5">
        <div className="container-5 w-container">
          <h2 className="cnannel-episode">Podcast 單集</h2>
          <div className="title-underline"/>
          <div className="div-block-10">
            <div className="w-row" style={{ width: '100%' }}>
              {episodes.map((episode) => (
                <div
                  className="w-col w-col-6 w-col-medium-12"
                  onClick={async () => {
                    if (
                      !episode.free_listen &&
                      (!session.isLoggedIn || !episode.is_purchased)
                    )
                      return;
                    await router.push('/episode/' + episode.id);
                  }}
                  key={episode.id}
                >
                  <div className="episode">
                    <div className="div-block-11">
                      <img
                        src={episode.image}
                        loading="lazy"
                        alt=""
                        className="image-5"
                      />
                      <div className="div-block-12">
                        <h4 className="heading-2">{episode.title}</h4>
                        <div className="tag-of-topic">
                          <p className="tag-3">
                            {renderEpisodeLength(episode)}
                          </p>

                          {!episode.free_listen &&
                          (!session.isLoggedIn || !episode.is_purchased) ? (
                              <p className="tag-3">付費解鎖</p>
                            ) : (
                              <div/>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopicsAndEpisodes;
